const Configuration = require('../app-context/configuration');
const S3_PREFIX = 'https://s3.amazonaws.com/near-peer/';
const MEDIA_PROXY_PATH = '/media-proxy';

const internals = {
    migrationKey: undefined
};

module.exports = {
    internals,

    getSizedImageUrl(path, size, options = {}) {

        if (!path) {
            return '';
        }

        const { normalizedPath, query } = internals.buildApiImageUrl(path);
        const urlWithVersion = internals.appendVersionQuery(
            `${Configuration.nearpeerApi.baseUrl()}/image/${size}/${normalizedPath}${query}`,
            options.version
        );

        return internals.appendMigrationQuery(urlWithVersion);
    },

    getOriginalImageUrl(path, options = {}) {

        if (!path) {
            return '';
        }

        const { normalizedPath, query } = internals.buildApiImageUrl(path);
        const urlWithVersion = internals.appendVersionQuery(
            `${Configuration.nearpeerApi.baseUrl()}/image/original/${normalizedPath}${query}`,
            options.version
        );

        return internals.appendMigrationQuery(urlWithVersion);
    },

    getDisplayImageUrl(url) {

        if (!url) {
            return '';
        }

        const sanitizedUrl = String(url).trim();

        if (internals.isDataUrl(sanitizedUrl)) {
            return sanitizedUrl;
        }

        const isAbsoluteUrl = internals.isHttpUrl(sanitizedUrl);
        const isS3Url = /https?:\/\/s3\.amazonaws\.com/.test(sanitizedUrl);

        if (isAbsoluteUrl) {
            return isS3Url ? module.exports.getOriginalImageUrl(sanitizedUrl) : module.exports.getMediaProxyUrl(sanitizedUrl);
        }

        return module.exports.getOriginalImageUrl(sanitizedUrl);
    },

    getMediaProxyUrl(url) {

        if (!url) {
            return '';
        }

        const sanitizedUrl = String(url).trim();

        if (!internals.shouldProxyMediaUrl(sanitizedUrl)) {
            return sanitizedUrl;
        }

        const apiBaseUrl = Configuration.nearpeerApi.baseUrl();

        if (!apiBaseUrl) {
            return sanitizedUrl;
        }

        return `${apiBaseUrl}${MEDIA_PROXY_PATH}?url=${encodeURIComponent(sanitizedUrl)}`;
    },

    getSchoolTileImageSrc(school) {

        if (!school) {
            return '';
        }

        if (school.svg) {
            return module.exports.getSvgDataUrl(school.svg);
        }

        return module.exports.getOriginalImageUrl(
            `https://s3.amazonaws.com/near-peer/school_logos/${school.slug}-tile.png`,
            { version: school.tileLogoVersion }
        );
    },

    /**
     * Converts SVG content to a data URL, handling both raw SVG and base64 content
     * @param {string} svgContent - Raw SVG string or base64 encoded content
     * @returns {string} Data URL for the SVG
     */
    getSvgDataUrl(svgContent) {

        if (!svgContent) {
            return '';
        }

        // If it's already a data URL, return as-is
        if (svgContent.startsWith('data:image/svg+xml')) {
            return svgContent;
        }

        // If it's raw SVG content (starts with <svg)
        if (svgContent.startsWith('<svg')) {
            const buff = Buffer.from(svgContent);
            const base64data = buff.toString('base64');
            return `data:image/svg+xml;base64,${base64data}`;
        }

        // Assume non-markup input is already base64-encoded SVG content.
        if (!svgContent.includes('<') && !svgContent.includes('>')) {
            return `data:image/svg+xml;base64,${svgContent}`;
        }

        // Fallback: treat as raw SVG and encode it
        const buff = Buffer.from(svgContent);
        const base64data = buff.toString('base64');
        return `data:image/svg+xml;base64,${base64data}`;
    }
};

internals.appendQueryParam = (url, key, value) => {

    if (value === undefined || value === null || value === '') {
        return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${key}=${encodeURIComponent(value)}`;
};

internals.appendVersionQuery = (url, version) => {

    return internals.appendQueryParam(url, 'version', version);
};

internals.appendMigrationQuery = (url) => {

    if (!process.env.ENABLE_IMAGE_CACHE_MIGRATION) {
        return url;
    }

    const migrationKey = internals.getMigrationKey();

    return internals.appendQueryParam(url, 'bustCache', migrationKey);
};

internals.getMigrationKey = () => {

    if (typeof internals.migrationKey !== 'undefined') {
        return internals.migrationKey;
    }

    if (typeof document === 'undefined' || typeof document.querySelectorAll !== 'function') {
        internals.migrationKey = null;
        return internals.migrationKey;
    }

    const scripts = Array.from(document.querySelectorAll('script[src]'));
    const hashedBundleScript = scripts
        .map((script) => script.getAttribute('src') || '')
        .find((src) => /\/js\/(?:app|vendor|\d+)\.[0-9a-f]{8}(?:\.chunk)?\.js(?:$|\?)/.test(src));

    if (!hashedBundleScript) {
        internals.migrationKey = null;
        return internals.migrationKey;
    }

    const match = hashedBundleScript.match(/\.([0-9a-f]{8})(?:\.chunk)?\.js(?:$|\?)/);
    internals.migrationKey = match ? match[1] : null;

    return internals.migrationKey;
};

internals.isHttpUrl = (url) => {

    return url.startsWith('http://') || url.startsWith('https://');
};

internals.isDataUrl = (url) => {

    return url.startsWith('data:');
};

internals.shouldProxyMediaUrl = (url) => {

    if (!internals.isHttpUrl(url)) {
        return false;
    }

    try {
        const parsedUrl = new URL(url);

        return !internals.isNearpeerHost(parsedUrl.hostname);
    }
    catch (err) {
        return false;
    }
};

internals.isNearpeerHost = (hostname) => {

    const normalizedHostname = String(hostname || '').toLowerCase();

    return (
        normalizedHostname === 'nearpeer.net' ||
        normalizedHostname.endsWith('.nearpeer.net') ||
        normalizedHostname === 'nearpeer.com' ||
        normalizedHostname.endsWith('.nearpeer.com')
    );
};

internals.normalizeImagePath = (path) => {

    if (!path) {
        return '';
    }

    return String(path)
        .replace(S3_PREFIX, '')
        .replace(/^\/+/, '')
        .split('?')[0]
        .trim();
};

internals.getImageQuery = (path) => {

    if (!path) {
        return '';
    }

    const rawPath = String(path).trim();
    const queryIndex = rawPath.indexOf('?');

    if (queryIndex === -1) {
        return '';
    }

    return rawPath.slice(queryIndex);
};

internals.buildApiImageUrl = (imagePath) => {

    const normalizedPath = internals.normalizeImagePath(imagePath);
    const query = internals.getImageQuery(imagePath);

    return {
        normalizedPath,
        query
    };
};
